/**
 * The survey's coordinate system for Geosoft (MB request R26, 24 Oct 2026): WGS 84 / UTM, the zone and hemisphere
 * from each block's lines file (`Zone=50 Hemi=S`), kept as the block's EPSG code (327zz south, 326zz north). The
 * Geosoft driver sets it on every database Teslyn creates and says when an existing database disagrees (the
 * F26048 databases were stamped 59S by a recorded GUI script; the survey is in 50S).
 */
import type { Block } from './model.js';

/** `32750` → `WGS 84 / UTM zone 50S`; null for anything that is not a WGS 84 UTM code. */
export function utmZoneName(epsg: number | null | undefined): string | null {
  if (epsg === null || epsg === undefined) return null;
  if (epsg >= 32601 && epsg <= 32660) return `WGS 84 / UTM zone ${epsg - 32600}N`;
  if (epsg >= 32701 && epsg <= 32760) return `WGS 84 / UTM zone ${epsg - 32700}S`;
  return null;
}

/**
 * The EPSG code a flight database (or `Total.gdb`) should carry: the one zone the survey blocks' lines files agree
 * on, preferring the blocks the flight touched (`digits`, the first digit of its line numbers) when they are known.
 * Null when no block has a zone or they disagree, so nothing is checked rather than something wrong.
 */
export function expectedEpsg(
  blocks: readonly Pick<Block, 'digit' | 'kind' | 'epsg'>[],
  digits?: ReadonlySet<string>,
): number | null {
  const survey = blocks.filter((b) => b.kind === 'survey' && utmZoneName(b.epsg) !== null);
  const flown = digits ? survey.filter((b) => digits.has(b.digit)) : [];
  const chosen = flown.length > 0 ? flown : survey;
  const codes = new Set(chosen.map((b) => b.epsg as number));
  return codes.size === 1 ? [...codes][0]! : null;
}
